import * as THREE from 'three';

export class ThreeGeomFactory {

    /**
     * Creates a rounded box geometry with beveled edges along the extrusion direction.
     * This creates truly rounded 3D edges, not just rounded corners in 2D.
     * @param width The width of the box (X-axis).
     * @param height The height of the box (Y-axis).
     * @param depth The depth of the box (Z-axis).
     * @param cornerRadius The radius of the corners in the base shape.
     * @param edgeRadius The radius of the beveled edges along the extrusion.
     * @param resolution A multiplier for the smoothness.
     * @returns An ExtrudeGeometry with rounded 3D edges.
     */
    public static createRoundedBoxWithBeveledEdges(
        width: number,
        height: number,
        depth: number,
        cornerRadius: number = 0.1,
        edgeRadius: number = 0.05,
        resolution: number = 1.0,
        _textureAspectRatio: number | null = null,
        flipBacksideX: boolean = true,
        useBacksideSideMaterial: boolean = false,

    ): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();
        const x = -width / 2;
        const y = -height / 2;
        const w = width;
        const h = height;
        const r = Math.min(cornerRadius, w / 2, h / 2); // Clamp corner radius

        // Create the rounded rectangle shape
        shape.moveTo(x + r, y);
        shape.lineTo(x + w - r, y);
        shape.quadraticCurveTo(x + w, y, x + w, y + r);
        shape.lineTo(x + w, y + h - r);
        shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        shape.lineTo(x + r, y + h);
        shape.quadraticCurveTo(x, y + h, x, y + h - r);
        shape.lineTo(x, y + r);
        shape.quadraticCurveTo(x, y, x + r, y);

        // Clamp edge radius to reasonable limits
        const maxEdgeRadius = Math.min(depth / 4, width / 8, height / 8);
        const clampedEdgeRadius = Math.min(edgeRadius, maxEdgeRadius);

        const extrudeSettings = {
            steps: Math.max(2, Math.ceil(4 * resolution)), // More steps for smoother bevels
            depth: depth - (2 * clampedEdgeRadius), // Reduce depth by bevel amount
            bevelEnabled: true,
            bevelThickness: clampedEdgeRadius,
            bevelSize: clampedEdgeRadius,
            bevelOffset: 0,
            bevelSegments: Math.max(2, Math.ceil(6 * resolution)), // Smooth bevel segments
            curveSegments: Math.max(1, Math.ceil(8 * resolution)),
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        
        // --- Material Groups ---
        // ExtrudeGeometry with bevel creates multiple groups
        // Material assignment is handled by shape classes (QuaderShape, etc.)
        //console.log(`🔍 [ThreeGeomFactory] createRoundedBoxWithBeveledEdges: Created ${geometry.groups.length} groups`);
        geometry.groups.forEach((group, i) => {
            //console.log(`  Group[${i}]: start=${group.start}, count=${group.count}, faces=${Math.round(group.count/3)}`);
        });
        
        geometry.center(); // Center the geometry

        // --- Enhanced UV Mapping for beveled geometry ---
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            // Enhanced UV mapping that handles beveled edges better
            if (Math.abs(n.z) > 0.7) { // Front or back face (allow for bevel normals)
                if (n.z > 0) { // Front face
                    u = (p.x / width) + 0.5;
                } else { // Back face, flip U coordinate conditionally
                    u = flipBacksideX ? (0.5 - (p.x / width)) : ((p.x / width) + 0.5);
                }
                v = (p.y / height) + 0.5;
            } else if (Math.abs(n.x) > Math.abs(n.y)) { // Left or right face (including bevels)
                u = (p.z / depth) + 0.5;
                v = (p.y / height) + 0.5;
            } else { // Top or bottom face (including bevels)
                u = (p.x / width) + 0.5;
                v = (p.z / depth) + 0.5;
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a rounded box geometry using an extruded shape. This is simpler and more robust.
     * @param width The width of the box (X-axis).
     * @param height The height of the box (Y-axis).
     * @param depth The depth of the box (Z-axis).
     * @param radius The radius of the corners.
     * @param resolution A multiplier for the smoothness.
     * @returns An ExtrudeGeometry for the rounded box.
     */
    public static createRoundedBox(
        width: number, 
        height: number, 
        depth: number, 
        radius: number, 
        resolution: number = 1.0,
        _textureAspectRatio: number | null = null,
        flipBacksideX: boolean = true,
        useBacksideSideMaterial: boolean = false,

    ): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();
        const x = -width / 2;
        const y = -height / 2;
        const w = width;
        const h = height;
        const r = Math.min(radius, w / 2, h / 2); // Clamp radius

        shape.moveTo(x + r, y);
        shape.lineTo(x + w - r, y);
        shape.quadraticCurveTo(x + w, y, x + w, y + r);
        shape.lineTo(x + w, y + h - r);
        shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        shape.lineTo(x + r, y + h);
        shape.quadraticCurveTo(x, y + h, x, y + h - r);
        shape.lineTo(x, y + r);
        shape.quadraticCurveTo(x, y, x + r, y);

        const extrudeSettings = {
            steps: 1,
            depth: depth, // The shape is 2D, so depth is the full depth.
            bevelEnabled: false, // Bevel is handled by the shape itself, no need for three's bevel
            curveSegments: Math.max(1, Math.ceil(8 * resolution)), // Use resolution with a base of 8
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        
        // --- Assign Material Groups ---
        // Material assignment is handled in Mesh3D.assignMaterialGroups()
        // Set default indices: front=0, back=1, sides=2
        //console.log(`🔍 [ThreeGeomFactory] Created ExtrudeGeometry with ${geometry.groups.length} groups`);
        geometry.groups.forEach((group, i) => {
            //console.log(`  Group[${i}]: faces=${group.count/3}, materialIndex=${group.materialIndex}`);
        });
        
        // Material assignment is handled in Mesh3D.assignMaterialGroups()
        // Don't set materialIndex here to avoid conflicts
        
        geometry.center(); // Center the geometry

        // --- UV Mapping ---
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            // Check the normal to determine the face direction
            if (Math.abs(n.z) > 0.99) { // Front or back face
                if (n.z > 0) { // Front face
                    u = (p.x / width) + 0.5;
                } else { // Back face, flip U coordinate
                    u = flipBacksideX ? (0.5 - (p.x / width)) : ((p.x / width) + 0.5);
                }
                v = (p.y / height) + 0.5;
            } else if (Math.abs(n.x) > 0.99) { // Left or right face
                u = (p.z / depth) + 0.5;
                v = (p.y / height) + 0.5;
            } else { // Top or bottom face
                u = (p.x / width) + 0.5;
                v = (p.z / depth) + 0.5;
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a rounded triangle geometry with beveled edges along the extrusion direction.
     * @param width The base width of the triangle.
     * @param height The height of the triangle.
     * @param depth The depth for extrusion.
     * @param cornerRadius The radius for rounding the triangle corners.
     * @param edgeRadius The radius for beveled edges along the extrusion.
     * @param resolution A multiplier for smoothness.
     * @returns An ExtrudeGeometry with beveled edges.
     */
    public static createRoundedTriangleWithBeveledEdges(
        width: number,
        height: number,
        depth: number,
        cornerRadius: number = 0.1,
        edgeRadius: number = 0.05,
        resolution: number = 1.0,
        textureAspectRatio: number | null = null,
        flipBacksideX: boolean = true,
        useBacksideSideMaterial: boolean = false,

    ): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();
        
        // Create triangle points
        const x1 = -width / 2;
        const x2 = width / 2;
        const x3 = 0;
        const y1 = -height / 3;
        const y2 = -height / 3;
        const y3 = (2 * height) / 3;
        
        const r = Math.min(cornerRadius, width / 4, height / 4);
        
        if (r > 0) {
            // Rounded triangle with corner radius
            const side1Length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
            const side2Length = Math.sqrt((x3 - x2) ** 2 + (y3 - y2) ** 2);
            const side3Length = Math.sqrt((x1 - x3) ** 2 + (y1 - y3) ** 2);
            
            const offset = Math.min(r, side1Length / 4, side2Length / 4, side3Length / 4);
            
            // Start at bottom-left corner
            shape.moveTo(x1 + offset, y1);
            shape.lineTo(x2 - offset, y2);
            shape.quadraticCurveTo(x2, y2, x2 - offset * 0.7, y2 + offset * 0.7);
            shape.lineTo(x3 + offset * 0.5, y3 - offset);
            shape.quadraticCurveTo(x3, y3, x3 - offset * 0.5, y3 - offset);
            shape.lineTo(x1 + offset * 0.7, y1 + offset * 0.7);
            shape.quadraticCurveTo(x1, y1, x1 + offset, y1);
        } else {
            // Regular triangle
            shape.moveTo(x1, y1);
            shape.lineTo(x2, y2);
            shape.lineTo(x3, y3);
            shape.lineTo(x1, y1);
        }

        // Clamp edge radius to reasonable limits
        const maxEdgeRadius = Math.min(depth / 4, width / 8, height / 8);
        const clampedEdgeRadius = Math.min(edgeRadius, maxEdgeRadius);

        const extrudeSettings = {
            steps: Math.max(2, Math.ceil(4 * resolution)),
            depth: depth - (2 * clampedEdgeRadius),
            bevelEnabled: true,
            bevelThickness: clampedEdgeRadius,
            bevelSize: clampedEdgeRadius,
            bevelOffset: 0,
            bevelSegments: Math.max(2, Math.ceil(6 * resolution)),
            curveSegments: Math.max(1, Math.ceil(8 * resolution)),
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        
        // --- Material Groups ---
        // ExtrudeGeometry creates multiple groups for front, back, and side faces
        // Material assignment is handled by shape classes (QuaderShape, etc.)
        //console.log(`🔍 [ThreeGeomFactory] ExtrudeGeometry: Created ${geometry.groups.length} groups`);
        geometry.groups.forEach((group, i) => {
            //console.log(`  Group[${i}]: start=${group.start}, count=${group.count}, faces=${Math.round(group.count/3)}`);
        });
        
        geometry.center();

        // Enhanced UV mapping with texture aspect ratio support
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            if (Math.abs(n.z) > 0.7) { // Front or back face (allow for bevel normals)
                // Calculate base UV coordinates
                let baseU = p.x / width;
                let baseV = p.y / height;
                
                // Flip X coordinate for back faces so texture appears correctly (not mirrored)
                if (n.z < 0 && flipBacksideX) { // Back face
                    baseU = -baseU;
                }
                
                // Apply texture aspect ratio if provided
                if (textureAspectRatio && textureAspectRatio !== 1.0) {
                    if (textureAspectRatio > 1.0) {
                        // Wider texture - scale U coordinate
                        u = baseU * textureAspectRatio + 0.5;
                        v = baseV + 0.5;
                    } else {
                        // Taller texture - scale V coordinate
                        u = baseU + 0.5;
                        v = baseV / textureAspectRatio + 0.5;
                    }
                } else {
                    u = baseU + 0.5;
                    v = baseV + 0.5;
                }
            } else {
                // Side faces - simple planar mapping
                const angle = Math.atan2(p.y, p.x);
                u = (angle + Math.PI) / (2 * Math.PI);
                v = (p.z / depth) + 0.5;
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a rounded triangle geometry using an extruded shape.
     * @param width The base width of the triangle.
     * @param height The height of the triangle.
     * @param depth The depth for extrusion.
     * @param radius The radius of the corners.
     * @returns An ExtrudeGeometry for the rounded triangle.
     */
    public static createRoundedTriangle(width: number, height: number, depth: number, radius: number, resolution: number = 1.0, flipBacksideX: boolean = true, useBacksideSideMaterial: boolean = false): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();

        // Isosceles triangle points
        const p1 = new THREE.Vector2(0, height / 2);         // Top
        const p2 = new THREE.Vector2(-width / 2, -height / 2); // Bottom-left
        const p3 = new THREE.Vector2(width / 2, -height / 2);  // Bottom-right

        // Clamp the radius to be at most half the length of the shortest side edge.
        // This prevents the rounded corners from overlapping.
        const edgeLength1 = p1.distanceTo(p2);
        const maxRadius = edgeLength1 / 2;
        const r = Math.min(radius, maxRadius);

        if (r <= 0.0001) {
            // Sharp corners if radius is effectively zero
            shape.moveTo(p1.x, p1.y);
            shape.lineTo(p2.x, p2.y);
            shape.lineTo(p3.x, p3.y);
        } else {
            // Calculate the interpolation factor 't' based on the clamped radius 'r'
            const t = r / edgeLength1;

            // Lerp to find points for quadratic curves
            const p1a = new THREE.Vector2().lerpVectors(p1, p3, t);
            const p1b = new THREE.Vector2().lerpVectors(p1, p2, t);
            const p2a = new THREE.Vector2().lerpVectors(p2, p1, t);
            const p2b = new THREE.Vector2().lerpVectors(p2, p3, t);
            const p3a = new THREE.Vector2().lerpVectors(p3, p2, t);
            const p3b = new THREE.Vector2().lerpVectors(p3, p1, t);
            
            // Draw the rounded shape
            shape.moveTo(p1b.x, p1b.y);
            shape.quadraticCurveTo(p1.x, p1.y, p1a.x, p1a.y);
            shape.lineTo(p3b.x, p3b.y);
            shape.quadraticCurveTo(p3.x, p3.y, p3a.x, p3a.y);
            shape.lineTo(p2b.x, p2b.y);
            shape.quadraticCurveTo(p2.x, p2.y, p2a.x, p2a.y);
        }
        
        shape.closePath();

        const extrudeSettings = {
            depth: depth,
            bevelEnabled: false,
            curveSegments: Math.max(1, Math.ceil(12 * resolution)), // Use resolution
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        geometry.center();

        // --- Assign Material Groups ---
        if (geometry.groups.length === 3) {
            geometry.groups[0].materialIndex = 0; // Front
            geometry.groups[1].materialIndex = useBacksideSideMaterial ? 1 : 0; // Back
            geometry.groups[2].materialIndex = 1; // Sides
        }

        // --- UV Mapping ---
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            // Check the normal to determine the face direction
            if (Math.abs(n.z) > 0.99) { // Front or back face
                if (n.z > 0) { // Front face
                    u = (p.x / width) + 0.5;
                } else { // Back face, flip U coordinate conditionally
                    u = flipBacksideX ? (0.5 - (p.x / width)) : ((p.x / width) + 0.5);
                }
                v = (p.y / height) + 0.5;
            } else if (Math.abs(n.x) > 0.5) { // Left or right angled faces
                // Use a mix of Y and Z for the U coordinate on the angled sides
                u = (p.z / depth) + 0.5;
                v = (p.y / height) + 0.5;
            } else { // Bottom face
                u = (p.x / width) + 0.5;
                v = (p.z / depth) + 0.5;
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a simplified rounded hexagon geometry with beveled edges - optimized version.
     * Uses a simpler hexagon construction for better beveling results.
     */
    public static createSimpleHexagonWithBeveledEdges(
        size: number,
        depth: number,
        cornerRadius: number = 0.1,
        edgeRadius: number = 0.05,
        resolution: number = 1.0,
        textureAspectRatio: number | null = null,
        flipBacksideX: boolean = true,
        useBacksideSideMaterial: boolean = false,

    ): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();

        // Create a simpler hexagon using direct path approach
        const hexRadius = size;
        const angleStep = Math.PI / 3; // 60 degrees
        
        // Generate all 6 hexagon vertices
        const points = [];
        for (let i = 0; i < 6; i++) {
            const angle = angleStep * i - Math.PI / 2; // Start from top
            const x = Math.cos(angle) * hexRadius;
            const y = Math.sin(angle) * hexRadius;
            points.push({ x, y });
        }
        
        // Start at the first point and draw all sides
        shape.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < 6; i++) {
            shape.lineTo(points[i].x, points[i].y);
        }
        
        // Close the path (automatically connects back to start)
        shape.closePath();

        // Very conservative edge radius limits
        const maxEdgeRadius = Math.min(depth / 8, size / 8);
        const clampedEdgeRadius = Math.min(edgeRadius, maxEdgeRadius);

        const extrudeSettings = {
            steps: 1, // Simple single-step extrusion
            depth: depth - (2 * clampedEdgeRadius),
            bevelEnabled: true,
            bevelThickness: clampedEdgeRadius,
            bevelSize: clampedEdgeRadius * 0.6, // Very subtle bevel
            bevelOffset: 0,
            bevelSegments: Math.max(1, Math.ceil(2 * resolution)), // Minimal bevel segments
            curveSegments: Math.max(1, Math.ceil(4 * resolution)),
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        
        // Material group assignment (front/back = 0, sides = 1)
        if (geometry.groups.length >= 3) {
            geometry.groups[0].materialIndex = 0; // Front face
            geometry.groups[1].materialIndex = useBacksideSideMaterial ? 1 : 0; // Back face
            for (let i = 2; i < geometry.groups.length; i++) {
                geometry.groups[i].materialIndex = 1; // Side faces
            }
        }
        
        geometry.center();

        // Simple UV mapping
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            // More precise face detection for beveled geometry
            const halfDepth = (depth - (2 * clampedEdgeRadius)) / 2;
            const isNearFrontOrBack = Math.abs(Math.abs(p.z) - halfDepth) < clampedEdgeRadius * 1.5;
            const hasVerticalNormal = Math.abs(n.z) > 0.8;
            
            if (hasVerticalNormal && isNearFrontOrBack) { // True front/back faces
                // Map to hexagon's circumscribed circle for better UV distribution
                // This ensures the texture fits the hexagon shape better
                let normalizedX = p.x / size; // -1 to 1
                const normalizedY = p.y / size; // -1 to 1
                
                // Flip X coordinate for back faces so texture appears correctly (not mirrored)
                if (n.z < 0 && flipBacksideX) { // Back face
                    normalizedX = -normalizedX;
                }
                
                // COVER-crop aspect correction. NOTE the division: for a wide texture we must
                // sample a NARROWER horizontal slice of it ( range 1/aspect ), not a wider one.
                // The previous version multiplied, pushing U to ±(0.5·aspect)+0.5 — far outside
                // [0,1] — which smeared the clamped edge pixels as vertical stripes on the pin.
                if (textureAspectRatio && textureAspectRatio !== 1.0) {
                    if (textureAspectRatio > 1.0) {
                        // Wider texture: sample the central horizontal slice (cover)
                        u = (normalizedX * 0.5 / textureAspectRatio) + 0.5;
                        v = (normalizedY * 0.5) + 0.5;
                    } else {
                        // Taller texture: sample the central vertical slice (cover)
                        u = (normalizedX * 0.5) + 0.5;
                        v = (normalizedY * 0.5 * textureAspectRatio) + 0.5;
                    }
                } else {
                    // Map to 0-1 range, centered
                    u = (normalizedX * 0.5) + 0.5;
                    v = (normalizedY * 0.5) + 0.5;
                }
            } else {
                // Side faces - simple cylindrical mapping
                const angle = Math.atan2(p.y, p.x);
                u = (angle + Math.PI) / (2 * Math.PI);
                v = (p.z / depth) + 0.5;
            }

            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }

        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a rounded hexagon geometry with beveled edges along the extrusion direction.
     * @param size The size (circumradius) of the hexagon.
     * @param depth The depth for extrusion.
     * @param cornerRadius The radius of the corners.
     * @param edgeRadius The radius for beveled edges along the extrusion.
     * @param resolution A multiplier for the smoothness.
     * @param textureAspectRatio The aspect ratio of the texture (width/height) for proper UV mapping.
     * @returns An ExtrudeGeometry with beveled edges.
     */
    public static createRoundedHexagonWithBeveledEdges(
        size: number,
        depth: number,
        cornerRadius: number = 0.1,
        edgeRadius: number = 0.05,
        resolution: number = 1.0,
        textureAspectRatio: number | null = null,
        flipBacksideX: boolean = true,
        useBacksideSideMaterial: boolean = false,

    ): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();

        // Regular hexagon points (6 sides)
        const points: THREE.Vector2[] = [];
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI * 2) / 6;
            const x = Math.cos(angle) * size;
            const y = Math.sin(angle) * size;
            points.push(new THREE.Vector2(x, y));
        }

        // Calculate edge length and clamp corner radius
        const edgeLength = points[0].distanceTo(points[1]);
        const maxRadius = edgeLength / 2;
        const r = Math.min(cornerRadius, maxRadius);

        if (r <= 0.0001) {
            // Sharp corners if radius is effectively zero
            shape.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                shape.lineTo(points[i].x, points[i].y);
            }
            shape.lineTo(points[0].x, points[0].y);
        } else {
            // Rounded corners
            for (let i = 0; i < points.length; i++) {
                const p1 = points[i];
                const p2 = points[(i + 1) % points.length];
                const p0 = points[(i - 1 + points.length) % points.length];

                const dir1 = new THREE.Vector2().subVectors(p1, p0).normalize();
                const dir2 = new THREE.Vector2().subVectors(p2, p1).normalize();

                const start = new THREE.Vector2().addVectors(p1, dir1.clone().multiplyScalar(r));
                const end = new THREE.Vector2().addVectors(p1, dir2.clone().multiplyScalar(r));

                if (i === 0) {
                    shape.moveTo(start.x, start.y);
                } else {
                    shape.lineTo(start.x, start.y);
                }
                shape.quadraticCurveTo(p1.x, p1.y, end.x, end.y);
            }
            shape.closePath();
        }

        // Clamp edge radius to reasonable limits
        const maxEdgeRadius = Math.min(depth / 6, size / 6); // More conservative limits for hexagons
        const clampedEdgeRadius = Math.min(edgeRadius, maxEdgeRadius);

        const extrudeSettings = {
            steps: Math.max(1, Math.ceil(2 * resolution)), // Fewer steps for hexagons
            depth: depth - (2 * clampedEdgeRadius),
            bevelEnabled: true,
            bevelThickness: clampedEdgeRadius,
            bevelSize: clampedEdgeRadius * 0.8, // Smaller bevel size for more subtle effect
            bevelOffset: 0,
            bevelSegments: Math.max(1, Math.ceil(3 * resolution)), // Fewer bevel segments
            curveSegments: Math.max(1, Math.ceil(6 * resolution)),
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        
        // --- Material Groups ---
        // ExtrudeGeometry creates multiple groups for front, back, and side faces
        // Material assignment is handled by shape classes (QuaderShape, etc.)
        //console.log(`🔍 [ThreeGeomFactory] ExtrudeGeometry: Created ${geometry.groups.length} groups`);
        geometry.groups.forEach((group, i) => {
            //console.log(`  Group[${i}]: start=${group.start}, count=${group.count}, faces=${Math.round(group.count/3)}`);
        });
        
        geometry.center();

        // Enhanced UV mapping with texture aspect ratio support
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            // More precise face detection for beveled geometry
            // Check both normal direction AND vertex position to identify true front/back faces
            const halfDepth = (depth - (2 * clampedEdgeRadius)) / 2;
            const isNearFrontOrBack = Math.abs(Math.abs(p.z) - halfDepth) < clampedEdgeRadius * 1.5;
            const hasVerticalNormal = Math.abs(n.z) > 0.8;
            
            if (hasVerticalNormal && isNearFrontOrBack) { // True front/back faces
                // Map to hexagon's circumscribed circle for better UV distribution
                // This ensures the texture fits the hexagon shape better
                let normalizedX = p.x / size; // -1 to 1
                const normalizedY = p.y / size; // -1 to 1
                
                // Flip X coordinate for back faces so texture appears correctly (not mirrored)
                if (n.z < 0 && flipBacksideX) { // Back face
                    normalizedX = -normalizedX;
                }
                
                // COVER-crop aspect correction (division, not multiplication — see
                // createSimpleHexagonWithBeveledEdges for the stripe-smear bug this fixes).
                if (textureAspectRatio && textureAspectRatio !== 1.0) {
                    if (textureAspectRatio > 1.0) {
                        // Wider texture: sample the central horizontal slice (cover)
                        u = (normalizedX * 0.5 / textureAspectRatio) + 0.5;
                        v = (normalizedY * 0.5) + 0.5;
                    } else {
                        // Taller texture: sample the central vertical slice (cover)
                        u = (normalizedX * 0.5) + 0.5;
                        v = (normalizedY * 0.5 * textureAspectRatio) + 0.5;
                    }
                } else {
                    // Map to 0-1 range, centered
                    u = (normalizedX * 0.5) + 0.5;
                    v = (normalizedY * 0.5) + 0.5;
                }
            } else {
                // Side faces (including bevels) - simple cylindrical mapping
                const angle = Math.atan2(p.y, p.x);
                u = (angle + Math.PI) / (2 * Math.PI);
                v = (p.z / depth) + 0.5;
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a rounded hexagon geometry using an extruded shape.
     * @param size The size (circumradius) of the hexagon.
     * @param depth The depth for extrusion.
     * @param radius The radius of the corners.
     * @param resolution A multiplier for the smoothness.
     * @param textureAspectRatio The aspect ratio of the texture (width/height) for proper UV mapping.
     * @returns An ExtrudeGeometry for the rounded hexagon.
     */
    public static createRoundedHexagon(size: number, depth: number, radius: number, resolution: number = 1.0, textureAspectRatio: number | null = null, flipBacksideX: boolean = true, useBacksideSideMaterial: boolean = false, textureFit: string = 'cover'): THREE.ExtrudeGeometry {
        const shape = new THREE.Shape();

        // Regular hexagon points (6 sides)
        const points: THREE.Vector2[] = [];
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI * 2) / 6;
            const x = Math.cos(angle) * size;
            const y = Math.sin(angle) * size;
            points.push(new THREE.Vector2(x, y));
        }

        // Calculate edge length and clamp radius
        const edgeLength = points[0].distanceTo(points[1]);
        const maxRadius = edgeLength / 2;
        const r = Math.min(radius, maxRadius);

        if (r <= 0.0001) {
            // Sharp corners if radius is effectively zero
            shape.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                shape.lineTo(points[i].x, points[i].y);
            }
        } else {
            // Calculate the interpolation factor 't' based on the clamped radius 'r'
            const t = r / edgeLength;

            // Create rounded corners
            const roundedPoints: { start: THREE.Vector2, control: THREE.Vector2, end: THREE.Vector2 }[] = [];
            
            for (let i = 0; i < points.length; i++) {
                const current = points[i];
                const next = points[(i + 1) % points.length];
                const prev = points[(i - 1 + points.length) % points.length];
                
                // Calculate points for the quadratic curve
                const startPoint = new THREE.Vector2().lerpVectors(current, prev, t);
                const endPoint = new THREE.Vector2().lerpVectors(current, next, t);
                
                roundedPoints.push({
                    start: startPoint,
                    control: current,
                    end: endPoint
                });
            }
            
            // Draw the rounded shape
            shape.moveTo(roundedPoints[0].start.x, roundedPoints[0].start.y);
            
            for (let i = 0; i < roundedPoints.length; i++) {
                const current = roundedPoints[i];
                const next = roundedPoints[(i + 1) % roundedPoints.length];
                
                // Draw quadratic curve for this corner
                shape.quadraticCurveTo(current.control.x, current.control.y, current.end.x, current.end.y);
                
                // Draw line to next corner start (if not the last iteration)
                if (i < roundedPoints.length - 1) {
                    shape.lineTo(next.start.x, next.start.y);
                }
            }
        }
        
        shape.closePath();

        const extrudeSettings = {
            depth: depth,
            bevelEnabled: false,
            curveSegments: Math.max(1, Math.ceil(12 * resolution)),
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        geometry.center();

        // --- Assign Material Groups ---
        if (geometry.groups.length === 3) {
            geometry.groups[0].materialIndex = 0; // Front
            geometry.groups[1].materialIndex = useBacksideSideMaterial ? 1 : 0; // Back
            geometry.groups[2].materialIndex = 1; // Sides
        }

        // --- UV Mapping with Aspect Ratio Support ---
        ////console.log(`🔍 ThreeGeomFactory.createRoundedHexagon: applying aspect ratio ${textureAspectRatio}`);
        
        const pos = geometry.attributes.position;
        const normals = geometry.attributes.normal;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            const n = new THREE.Vector3(normals.getX(i), normals.getY(i), normals.getZ(i));
            const p = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            
            let u, v;
            
            // Check the normal to determine the face direction
            if (Math.abs(n.z) > 0.99) { // Front or back face
                // Map to hexagon's circumscribed circle for better UV distribution
                const normalizedX = p.x / size; // -1 to 1
                const normalizedY = p.y / size; // -1 to 1
                
                if (n.z > 0) { // Front face
                    u = (normalizedX * 0.5) + 0.5;
                } else { // Back face, flip U coordinate if enabled
                    u = flipBacksideX ? (0.5 - (normalizedX * 0.5)) : ((normalizedX * 0.5) + 0.5);
                }
                v = (normalizedY * 0.5) + 0.5;
                
                const a = (textureAspectRatio !== null && textureAspectRatio > 0) ? textureAspectRatio : 1;
                if (textureFit === 'contain') {
                    // CONTAIN: groesstes achsparalleles Rechteck mit Bild-Seitenverhaeltnis,
                    // das in das Flat-Top-Sechseck (Umkreisradius size) passt:
                    //   h = min(2*sqrt(3)*s / (1 + sqrt(3)*a), sqrt(3)*s),  w = a*h
                    // Das Bild [0..1] wird auf dieses Rechteck gelegt; UVs ausserhalb
                    // laufen ueber [0..1] hinaus — der Aufrufer stellt ClampToEdge,
                    // bei Freistellern ist die geklemmte Randfarbe transparent.
                    const S3 = Math.sqrt(3);
                    const h = Math.min((2 * S3 * size) / (1 + S3 * a), S3 * size);
                    const w = a * h;
                    const px = (n.z > 0 || !flipBacksideX) ? p.x : -p.x;
                    u = px / w + 0.5;
                    v = p.y / h + 0.5;
                } else if (a > 1) {
                    // COVER: Bild breiter als hoch — links/rechts beschneiden
                    const cropFactor = 1 / a;
                    const offset = (1 - cropFactor) / 2;
                    u = offset + u * cropFactor;
                } else if (a < 1) {
                    // COVER: Bild hoeher als breit — oben/unten beschneiden
                    const cropFactor = a;
                    const offset = (1 - cropFactor) / 2;
                    v = offset + v * cropFactor;
                }
            } else { // Side faces
                u = (p.z / depth) + 0.5;
                v = (p.y / size) * 0.5 + 0.5;
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }
        
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        return geometry;
    }

    /**
     * Creates a flat 2D plane geometry with rounded corners.
     * @param width The width of the plane.
     * @param height The height of the plane.
     * @param radius The radius of the corners.
     * @param resolution A multiplier for the curve segments.
     * @returns A BufferGeometry for the rounded plane.
     */
    public static createRoundedPlane(
        width: number, 
        height: number, 
        radius: number, 
        resolution: number = 1.0,
        geometryAspectRatio: number = 1.0,
        textureAspectRatio: number | null = null
    ): THREE.BufferGeometry {
        const shape = new THREE.Shape();
        const x = -width / 2;
        const y = -height / 2;
        const w = width;
        const h = height;
        const r = Math.min(radius, width / 2, height / 2); // Clamp radius

        if (r < 0.0001) {
            // Sharp corners
            shape.moveTo(x, y);
            shape.lineTo(x + w, y);
            shape.lineTo(x + w, y + h);
            shape.lineTo(x, y + h);
        } else {
            // Rounded corners
            shape.moveTo(x + r, y);
            shape.lineTo(x + w - r, y);
            shape.quadraticCurveTo(x + w, y, x + w, y + r);
            shape.lineTo(x + w, y + h - r);
            shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            shape.lineTo(x + r, y + h);
            shape.quadraticCurveTo(x, y + h, x, y + h - r);
            shape.lineTo(x, y + r);
            shape.quadraticCurveTo(x, y, x + r, y);
        }

        const segments = Math.max(3, Math.ceil(12 * resolution));
        const geometry = new THREE.ShapeGeometry(shape, segments);

        // Compute UVs with optional cropping for aspect ratio correction
        const pos = geometry.attributes.position;
        const uv = new Float32Array(pos.count * 2);

        for (let i = 0; i < pos.count; i++) {
            // Base UV mapping: world coordinates to 0-1 range
            let u = (pos.getX(i) / width) + 0.5;   // -0.5..0.5 → 0..1
            let v = (pos.getY(i) / height) + 0.5;  // -0.5..0.5 → 0..1
            
            // Apply cropping if texture aspect ratio differs from geometry
            if (textureAspectRatio && textureAspectRatio > 0) {
                const aspectRatio = geometryAspectRatio / textureAspectRatio;
                
                if (aspectRatio > 1.1) {
                    // Geometry is wider than texture → crop texture height (top/bottom)
                    const cropFactor = 1.0 / aspectRatio;
                    const offset = (1.0 - cropFactor) * 0.5;
                    v = v * cropFactor + offset;
                } else if (aspectRatio < 0.9) {
                    // Geometry is taller than texture → crop texture width (left/right)
                    const cropFactor = aspectRatio;
                    const offset = (1.0 - cropFactor) * 0.5;
                    u = u * cropFactor + offset;
                }
                // If aspectRatio ≈ 1.0, no cropping needed
            }
            
            uv[i * 2] = u;
            uv[i * 2 + 1] = v;
        }

        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));

        // Center the geometry to match other shapes
        geometry.center();

        return geometry;
    }


}